import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import i18n from 'i18next';
import { useParams } from 'react-router-dom';

import PageHeader from 'components/Header';
import CodeCard from 'components/CodeCard';
import LinkCard from 'components/LinkCard';
import ParamsTable from 'components/ParamsTable';
import Loader from 'components/Loader';
import Toaster from 'components/Toaster';
import DocumentCard from 'components/DocumentCard';
import { getDetails } from './api/getDetails';

import styles from './style.module.scss';

const RESPONSE_TEXT_COLOR = '#82FFB4';

const Service = () => {
  const { language } = i18n;
  const { t } = useTranslation('global');
  const { serviceId } = useParams();

  const [details, setDetails] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isError, setIsError] = useState(false);

  const handleGetDetails = useCallback(async () => {
    try {
      setIsLoading(true);
      setIsError(false);

      const response = await getDetails(serviceId);

      setDetails(response.data ?? null);
    } catch (err) {
      setIsError(true);
    } finally {
      setIsLoading(false);
    }
  }, [serviceId]);

  useEffect(() => {
    if (language && serviceId) {
      handleGetDetails();
    }
  }, [language, serviceId, handleGetDetails]);

  const { requestParams, responseParams, responseCodeParams } = useMemo(() => {
    const methodParams = details?.methodParams ?? [];

    return {
      requestParams: methodParams.filter(
        param => param.paramType === 'request',
      ),
      responseParams: methodParams.filter(
        param => param.paramType === 'response',
      ),
      responseCodeParams: methodParams.filter(
        param => param.paramType === 'response_code',
      ),
    };
  }, [details?.methodParams]);

  const urlButtons = useMemo(() => {
    if (!details?.urlDev || !details?.urlProduction) {
      return null;
    }

    return [
      { label: t('testButton'), link: details.urlDev },
      { label: t('prodButton'), link: details.urlProduction },
    ];
  }, [details?.urlDev, details?.urlProduction, t]);

  const exampleRequestUrlButtons = useMemo(() => {
    const exampleRequestRu = details?.exampleRequestUrls?.exampleRequestRu;

    if (!exampleRequestRu) {
      return null;
    }

    return [{ label: 'Рус', link: exampleRequestRu }];
  }, [details?.exampleRequestUrls?.exampleRequestRu]);

  const exampleButtons = useMemo(() => {
    if (!details?.exampleRequest || !details?.exampleRequestProd) {
      return null;
    }

    return [
      { label: t('testButton'), link: details.exampleRequest },
      { label: t('prodButton'), link: details.exampleRequestProd },
    ];
  }, [details?.exampleRequest, details?.exampleRequestProd, t]);

  const codeCards = useMemo(() => {
    if (!details) {
      return [];
    }

    const cards = [];

    const isSoap = details.apiType === 'SOAP';
    const isRest = details.apiType === 'REST';
    const isPostOrPut =
      details.methodType === 'POST' || details.methodType === 'PUT';

    if (isSoap && details.requestBody) {
      cards.push({
        script: details.requestBody,
        title: t('requestBodyTitle'),
      });
    }

    if (
      (isSoap || (isRest && isPostOrPut && !details.requestBody)) &&
      details.exampleRequest
    ) {
      cards.push({
        script: details.exampleRequest,
        title: t('exampleRequestTitle'),
      });
    }

    if (
      (isSoap || (isRest && isPostOrPut && !details.requestBody)) &&
      details.exampleResponse
    ) {
      cards.push({
        script: details.exampleResponse,
        title: t('exampleResponseTitle'),
        textColor: RESPONSE_TEXT_COLOR,
      });
    }

    return cards;
  }, [details, t]);

  if (isLoading) {
    return <Loader />;
  }

  return (
    <div>
      {isError && <Toaster errorMessage="Ошибка" />}

      {details && (
        <div className={styles.container}>
          <PageHeader
            header={details.methodName}
            description={details.description}
            methodType={details.methodType}
          />

          <div className={styles.content}>
            <div className={styles.layout2}>
              <div className={styles.leftContent}>
                <div className={styles.urlLinks}>
                  {urlButtons && <LinkCard text="URL" buttons={urlButtons} />}

                  {!urlButtons && details.urlUnified && (
                    <LinkCard
                      text="URL"
                      defaultLink={details.urlUnified}
                      headers={details.headers || undefined}
                    />
                  )}
                </div>

                <div className={styles.exampleLinks}>
                  {exampleRequestUrlButtons && (
                    <LinkCard
                      text={t('isExampleTitle')}
                      buttons={exampleRequestUrlButtons}
                    />
                  )}

                  {exampleButtons && (
                    <LinkCard
                      text={t('isExampleTitle')}
                      buttons={exampleButtons}
                    />
                  )}

                  {details.apiType === 'REST' &&
                    details.methodType === 'GET' &&
                    !details.exampleRequestUrls?.exampleRequestRu &&
                    !details.requestBody &&
                    !exampleButtons &&
                    details.exampleRequest && (
                      <LinkCard
                        text={t('isExampleTitle')}
                        defaultLink={details.exampleRequest}
                      />
                    )}
                </div>

                {requestParams.length > 0 && (
                  <ParamsTable
                    data={requestParams}
                    title={t('paramsRequestTitle')}
                  />
                )}

                {responseParams.length > 0 && (
                  <ParamsTable
                    data={responseParams}
                    title={t('paramsResponseTitle')}
                  />
                )}

                {responseCodeParams.length > 0 && (
                  <ParamsTable
                    data={responseCodeParams}
                    title={t('paramsResponseCodeTitle')}
                  />
                )}
              </div>

              <div className={styles.rightContent}>
                {details.documentationUrl && (
                  <DocumentCard documentUrl={details.documentationUrl} />
                )}

                {details.apiType === 'REST' &&
                  details.mapping &&
                  !details.headers && (
                    <LinkCard
                      text={t('mappingTitle')}
                      description={details.mappingDescription || undefined}
                      defaultLink={details.mapping}
                    />
                  )}

                {codeCards.map(code => (
                  <CodeCard
                    key={`${code.title}-${code.script}`}
                    script={code.script}
                    title={code.title}
                    textColor={code.textColor}
                  />
                ))}

                {details.apiType === 'REST' &&
                  details.methodType === 'GET' &&
                  details.exampleResponse && (
                    <CodeCard
                      script={details.exampleResponse}
                      title={t('exampleResponseTitle')}
                      textColor={RESPONSE_TEXT_COLOR}
                    />
                  )}

                {details.structureResponse && (
                  <CodeCard
                    script={details.structureResponse}
                    title={t('structureResponseTitle')}
                    textColor={RESPONSE_TEXT_COLOR}
                  />
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Service;
